import Pusher from "pusher-js";
import appConfig from "configs/app.config";
import { DOWNLOAD_CHANNEL_EVENTS, PUSHER_CHANNEL_EVENTS } from "./types";

const BOUND_USER_CHANNEL_EVENTS = [
  ...DOWNLOAD_CHANNEL_EVENTS,
  ...PUSHER_CHANNEL_EVENTS,
];

class PusherService {
  constructor() {
    this.pusher = null;
    this.channel = null;
    this.channelName = null;
    this.accountId = null;
    this.userId = null;
    this.channelEventsBound = false;
    this.connectionHandlersAttached = false;
    this.boundEvents = new Set();
    this.listeners = {
      "file-ready": new Set(),
      "download-completed": new Set(),
      "encrypted-declaration-progress": new Set(),
      "record-generation-progress": new Set(),
      notification: new Set(),
      "admin-notification": new Set(),
    };
    this.additionalChannels = new Map();
    this.connectionState = "disconnected";
    this.lastError = null;

    if (typeof window !== "undefined") {
      window.__PUSHER_CONNECTION_STATS__ = () => this.getConnectionStats();
    }
  }

  getConnectionStats() {
    return {
      activeConnections: this.pusher ? 1 : 0,
      connectionState: this.connectionState,
      channelName: this.channelName,
      accountId: this.accountId,
      userId: this.userId,
      isConnectionOpen: this.isConnectionOpen(),
      isChannelSubscribed: this.isChannelSubscribed(),
      boundEvents: Array.from(this.boundEvents),
      listenerCounts: Object.fromEntries(
        Object.entries(this.listeners).map(([eventName, set]) => [
          eventName,
          set.size,
        ]),
      ),
    };
  }

  logConnectionStats(action) {
    if (process.env.NODE_ENV !== "development") {
      return;
    }

    const stats = this.getConnectionStats();
    console.info(
      `[Pusher] ${action} | active connections: ${stats.activeConnections}`,
      stats,
    );
  }

  static buildChannelName(accountId, userId) {
    return `${accountId}-${userId}`;
  }

  getConfig() {
    const config = appConfig.pusher || {};
    return {
      key: config.key ?? "",
      cluster: config.cluster ?? "us2",
      enabled: config.enabled !== false,
    };
  }

  parsePayload(raw) {
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    }

    return raw;
  }

  dispatch(eventName, payload) {
    const listeners = this.listeners[eventName];
    if (!listeners) {
      return;
    }

    listeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.error(`[Pusher] Handler failed for "${eventName}"`, error);
      }
    });
  }

  bindChannelEvent(eventName) {
    if (!this.channel || this.boundEvents.has(eventName)) {
      return;
    }

    this.channel.bind(eventName, (data) => {
      const payload = this.parsePayload(data);
      this.dispatch(eventName, payload);
    });

    this.boundEvents.add(eventName);
  }

  bindChannelEventsOnce() {
    if (!this.channel || this.channelEventsBound) {
      return;
    }

    const events = new Set([
      ...BOUND_USER_CHANNEL_EVENTS,
      ...Object.keys(this.listeners),
    ]);

    events.forEach((eventName) => this.bindChannelEvent(eventName));

    this.channelEventsBound = true;

    if (process.env.NODE_ENV === "development") {
      console.info(
        "[Pusher] bindChannelEventsOnce | bound events:",
        Array.from(this.boundEvents),
      );
    }
  }

  ensureEventBound(eventName) {
    if (!this.channel || !this.channelEventsBound) {
      return;
    }

    if (this.boundEvents.has(eventName)) {
      return;
    }

    this.bindChannelEvent(eventName);

    if (process.env.NODE_ENV === "development") {
      console.info(
        `[Pusher] lazily bound event "${eventName}" — bound events:`,
        Array.from(this.boundEvents),
      );
    }
  }

  bindDownloadChannelEventsOnce() {
    this.bindChannelEventsOnce();
  }

  attachConnectionHandlers() {
    if (!this.pusher || this.connectionHandlersAttached) {
      return;
    }

    this.pusher.connection.bind("state_change", (states) => {
      this.connectionState = states.current;
      this.logConnectionStats(`state: ${states.previous} → ${states.current}`);
    });

    this.pusher.connection.bind("error", (error) => {
      this.lastError = error?.error?.message ?? "Pusher connection error";
      console.error("[Pusher]", this.lastError);
    });

    this.connectionHandlersAttached = true;
  }

  connect(accountId, userId) {
    this.subscribeToUserChannel(accountId, userId);
  }

  initConnection(accountId, userId) {
    this.connect(accountId, userId);
  }

  subscribeToUserChannel(accountId, userId) {
    const config = this.getConfig();

    if (!config.enabled || !config.key) {
      return;
    }

    const numericAccountId = Number(accountId);
    const numericUserId = Number(userId);
    const nextChannelName = PusherService.buildChannelName(
      numericAccountId,
      numericUserId,
    );

    if (
      this.pusher &&
      this.channel &&
      this.channelName === nextChannelName &&
      this.accountId === numericAccountId &&
      this.userId === numericUserId &&
      this.channelEventsBound
    ) {
      this.logConnectionStats("subscribeToUserChannel (already active)");
      return;
    }

    if (
      this.pusher &&
      (this.accountId !== numericAccountId || this.userId !== numericUserId)
    ) {
      this.disconnect();
    }

    if (!this.pusher) {
      this.accountId = numericAccountId;
      this.userId = numericUserId;

      this.pusher = new Pusher(config.key, {
        cluster: config.cluster,
        enabledTransports: ["ws", "wss"],
      });

      this.attachConnectionHandlers();
    }

    if (this.channel && this.channelName !== nextChannelName) {
      this.unsubscribeFromUserChannel();
    }

    this.accountId = numericAccountId;
    this.userId = numericUserId;
    this.channelName = nextChannelName;
    this.channel = this.pusher.subscribe(nextChannelName);
    this.bindChannelEventsOnce();
    this.logConnectionStats(`subscribeToUserChannel (${nextChannelName})`);
  }

  subscribeToDownloadChannel(accountId, userId) {
    this.subscribeToUserChannel(accountId, userId);
  }

  unsubscribeFromUserChannel() {
    if (!this.channel && !this.channelName) {
      return;
    }

    const eventsToUnbind = new Set([
      ...BOUND_USER_CHANNEL_EVENTS,
      ...this.boundEvents,
    ]);

    eventsToUnbind.forEach((eventName) => {
      this.channel?.unbind(eventName);
    });

    if (this.pusher && this.channelName) {
      this.pusher.unsubscribe(this.channelName);
    }

    this.channel = null;
    this.channelName = null;
    this.channelEventsBound = false;
    this.boundEvents = new Set();
    this.logConnectionStats("unsubscribeFromUserChannel");
  }

  unsubscribeFromDownloadChannel() {
    this.unsubscribeFromUserChannel();
  }

  subscribe(eventName, listener) {
    if (!eventName || typeof listener !== "function") {
      return () => {};
    }

    if (!this.listeners[eventName]) {
      console.warn(
        `[Pusher] Unknown event "${eventName}" — registering on the fly`,
      );
      this.listeners[eventName] = new Set();
    }

    const set = this.listeners[eventName];
    set.add(listener);

    // Defense-in-depth: ensure the channel is binding this event. If the
    // channel is active but the event wasn’t in BOUND_USER_CHANNEL_EVENTS,
    // this binds it now instead of silently dropping every server emission.
    this.ensureEventBound(eventName);

    return () => {
      set.delete(listener);
    };
  }

  ensureClient() {
    const config = this.getConfig();
    if (!config.enabled || !config.key) {
      return false;
    }

    if (!this.pusher) {
      this.pusher = new Pusher(config.key, {
        cluster: config.cluster,
        enabledTransports: ["ws", "wss"],
      });
      this.attachConnectionHandlers();
    }

    return true;
  }

  subscribeToAdditionalChannel(channelName, events = []) {
    if (!channelName || !Array.isArray(events) || events.length === 0) {
      return;
    }

    if (!this.ensureClient()) {
      return;
    }

    let entry = this.additionalChannels.get(channelName);
    if (!entry) {
      const channel = this.pusher.subscribe(channelName);
      entry = { channel, events: new Set() };
      this.additionalChannels.set(channelName, entry);
    }

    events.forEach((eventName) => {
      if (entry.events.has(eventName)) {
        return;
      }
      entry.channel.bind(eventName, (data) => {
        const payload = this.parsePayload(data);
        this.dispatch(eventName, payload);
      });
      entry.events.add(eventName);
    });
  }

  unsubscribeFromAdditionalChannel(channelName, events = []) {
    const entry = this.additionalChannels.get(channelName);
    if (!entry) {
      return;
    }

    const eventSet =
      Array.isArray(events) && events.length ? new Set(events) : entry.events;

    eventSet.forEach((eventName) => {
      entry.channel.unbind(eventName);
      entry.events.delete(eventName);
    });

    if (entry.events.size === 0) {
      this.pusher?.unsubscribe(channelName);
      this.additionalChannels.delete(channelName);
    }
  }

  getConnectionState() {
    return this.connectionState;
  }

  getLastError() {
    return this.lastError;
  }

  getActiveChannelName() {
    return this.channelName;
  }

  isConnectionOpen() {
    return Boolean(this.pusher);
  }

  isChannelSubscribed() {
    return Boolean(this.channel);
  }

  isConnected() {
    return this.isConnectionOpen();
  }

  disconnect() {
    this.additionalChannels.forEach((entry, channelName) => {
      entry.events.forEach((eventName) => {
        entry.channel.unbind(eventName);
      });
      this.pusher?.unsubscribe(channelName);
    });
    this.additionalChannels.clear();

    const wasActive = Boolean(this.pusher);

    this.unsubscribeFromUserChannel();

    this.pusher?.disconnect();
    this.pusher = null;
    this.accountId = null;
    this.userId = null;
    this.connectionHandlersAttached = false;
    this.connectionState = "disconnected";
    this.lastError = null;

    Object.keys(this.listeners).forEach((eventName) => {
      this.listeners[eventName].clear();
    });

    if (wasActive) {
      this.logConnectionStats("disconnect");
    }
  }
}

export const pusherService = new PusherService();

export default pusherService;